<template>
  <div>
    <div class="filter-bar">
      <el-input
        v-model="search"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="Search serial number or model"
        style="width:260px"
      />
      <el-select
        v-model="status"
        size="small"
        clearable
        placeholder="All Statuses"
        style="width:160px"
        @change="loadPods"
      >
        <el-option
          v-for="item in statuses"
          :key="item"
          :label="item"
          :value="item"
        />
      </el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadPods"
        >Refresh</el-button
      >
      <span class="result-count">{{ filteredPods.length }} Smartz Pods</span>
      <el-button
        v-if="$permission('Installation Tool.Devices.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreate"
        >Add Smartz Pod</el-button
      >
    </div>

    <el-table
      :data="filteredPods"
      v-loading="loading"
      border
      class="app-table"
      :max-height="tableHeight"
    >
      <el-table-column label="Pod ID" min-width="220">
        <template slot-scope="{ row }">{{ podIdentifier(row) }}</template>
      </el-table-column>
      <el-table-column prop="model" label="Model" min-width="180" />
      <el-table-column prop="status" label="Status" width="140">
        <template slot-scope="{ row }"
          ><el-tag :type="statusType(row.status)" size="small">{{
            row.status
          }}</el-tag></template
        >
      </el-table-column>
      <el-table-column label="Actions" width="110" align="center">
        <template slot-scope="{ row }">
          <el-button
            v-if="$permission('Installation Tool.Devices.Edit')"
            type="danger"
            plain
            size="small"
            :disabled="row.status && row.status !== 'Unassigned'"
            @click="deletePod(row)"
            >Delete</el-button
          >
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      title="Add Smartz Pod"
      :visible.sync="showCreate"
      width="500px"
      @close="resetForm"
    >
      <el-form :model="form" ref="podForm" label-width="150px">
        <el-form-item label="Pod Serial Number" required>
          <div class="hex-input-group">
            <span class="hex-prefix">S00</span>
            <input
              v-for="(item, index) in bleHexChars"
              :key="index"
              :ref="'bH' + index"
              v-model="item.c"
              class="hex-char"
              maxlength="1"
              inputmode="text"
              autocomplete="off"
              @input="bHexIn(index)"
              @keydown="bHexDir(index, $event)"
            />
          </div>
          <div v-if="podIdError" class="input-error">Required</div>
        </el-form-item>
        <el-form-item label="Model" prop="model"
          ><el-input v-model="form.model" placeholder="Optional"
        /></el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="saving" @click="createPod"
          >ADD TO POOL</el-button
        >
        <el-button class="cancel-btn" @click="showCreate = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import loraTableHeight from '@/mixins/loraTableHeight'

const emptyForm = () => ({ model: '' })
const emptyHexChars = () => Array.from({ length: 4 }, () => ({ c: '' }))

export default {
  name: 'LoraSmartzPodPool',
  mixins: [loraTableHeight],
  data() {
    return {
      pods: [],
      loading: false,
      saving: false,
      search: '',
      status: '',
      statuses: ['Unassigned', 'Assigned', 'Commissioned'],
      showCreate: false,
      form: emptyForm(),
      bleHexChars: emptyHexChars(),
      podIdError: false
    }
  },
  computed: {
    podSerialNumber() {
      return 'S00' + this.bleHexChars.map(item => item.c).join('')
    },
    filteredPods() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.pods
      return this.pods.filter(pod =>
        [pod.deviceId, pod.serialNumber, pod.model].some(value =>
          String(value || '')
            .toLowerCase()
            .includes(query)
        )
      )
    }
  },
  mounted() {
    this.loadPods()
  },
  methods: {
    responseItems(response) {
      const body = response.data || []
      const items = body.data || body.items || body.Items || body
      return Array.isArray(items) ? items : []
    },
    async loadPods() {
      this.loading = true
      try {
        const response = await LoraApiService.getBleDevices(
          this.status ? { status: this.status } : {}
        )
        this.pods = this.responseItems(response)
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to load Smartz Pod pool'))
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.bleHexChars = emptyHexChars()
      this.podIdError = false
      this.showCreate = true
      this.$nextTick(() => this.focusHex('bH', 0))
    },
    resetForm() {
      this.$refs.podForm && this.$refs.podForm.resetFields()
      this.bleHexChars = emptyHexChars()
      this.podIdError = false
    },
    async createPod() {
      if (this.bleHexChars.some(item => !item.c)) {
        this.podIdError = true
        this.focusHex('bH', this.bleHexChars.findIndex(item => !item.c))
        return
      }
      this.saving = true
      try {
        await LoraApiService.createBleDevice({
          serialNumber: this.podSerialNumber,
          model: this.form.model || undefined
        })
        this.$message.success('Smartz Pod added to pool')
        this.showCreate = false
        this.loadPods()
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to add Smartz Pod'))
      } finally {
        this.saving = false
      }
    },
    focusHex(prefix, index) {
      const ref = this.$refs[prefix + index]
      const input = Array.isArray(ref) ? ref[0] : ref
      input && input.focus()
    },
    bHexIn(index) {
      const value = this.bleHexChars[index].c
        .replace(/[^0-9a-f]/gi, '')
        .toUpperCase()
        .slice(-1)
      this.bleHexChars[index].c = value
      this.podIdError = false
      if (value && index < 3) this.focusHex('bH', index + 1)
    },
    bHexDir(index, event) {
      if (
        event.key === 'Backspace' &&
        !this.bleHexChars[index].c &&
        index > 0
      ) {
        this.focusHex('bH', index - 1)
      } else if (event.key === 'ArrowLeft' && index > 0) {
        event.preventDefault()
        this.focusHex('bH', index - 1)
      } else if (event.key === 'ArrowRight' && index < 3) {
        event.preventDefault()
        this.focusHex('bH', index + 1)
      }
    },
    async deletePod(pod) {
      try {
        await this.$confirm(
          'Delete Smartz Pod ' + this.podIdentifier(pod) + ' from the pool?',
          'Confirm Delete',
          { type: 'warning', confirmButtonText: 'Delete' }
        )
      } catch {
        return
      }
      try {
        await LoraApiService.deleteBleDevice(pod.id)
        this.$message.success('Smartz Pod deleted')
        this.loadPods()
      } catch (error) {
        this.$message.error(loraError(error, 'Failed to delete Smartz Pod'))
      }
    },
    podIdentifier(pod) {
      return pod.deviceId || pod.serialNumber || '--'
    },
    statusType(status) {
      return (
        { Unassigned: 'info', Assigned: 'warning', Commissioned: 'success' }[
          status
        ] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  .result-count {
    margin-left: auto;
    color: $font-color;
    font-size: 13px;
  }
}
.hex-input-group {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  &:focus-within {
    border-color: #409eff;
  }
}
.hex-prefix,
.hex-char {
  font-family: monospace;
  font-size: 14px;
}
.hex-prefix {
  color: #909399;
  user-select: none;
}
.hex-char {
  width: 20px;
  padding: 0;
  border: 0;
  outline: 0;
  background: transparent;
  color: #303133;
  font-weight: 600;
  text-align: center;
  text-transform: uppercase;
}
.input-error {
  position: absolute;
  color: #f56c6c;
  font-size: 12px;
  line-height: 1;
  padding-top: 4px;
}
</style>
